import _ from 'lodash';
import React, { createContext } from 'react';
import { FieldValues } from 'react-hook-form';
import { ProductConfigurationResponse } from '../api/ConfigApi';
import { handleUnauthorizedResponse } from '../api/http';
import PolicyApi from '../api/PolicyApi';
import ProductApi from '../api/ProductApi';
import RenewalsApi from '../api/RenewalsApi';
import ResourcesAPI from '../components/admin/tenant/api/resources';
import getRenewalExposuresForm from '../components/fastForm/formBuilders/getRenewalExposuresForm';
import getRenewalForm from '../components/fastForm/formBuilders/getRenewalForm';
import {
	FormBuilder,
	FormShape,
	SaveFastForm,
	SaveOptions,
} from '../components/fastForm/types';
import endorsementRenewalUpdateBuilder from '../components/fastForm/updateBuilders/endorsementRenewalUpdateBuilder';
import { getHasPaymentPluginEnabled } from '../components/policy/util_sharedEndorsementAndRenewal';
import { successToast } from '../components/Toast';
import {
	FETCH_RENEWAL_FAILURE,
	LOAD_RENEWAL_FAILURE,
	RENEWAL_ACCEPT_FAILURE,
	RENEWAL_ACCEPT_SUCCESS,
	RENEWAL_DISCARD_FAILURE,
	RENEWAL_DISCARD_SUCCESS,
	RENEWAL_INVALIDATE_FAILURE,
	RENEWAL_INVALIDATE_SUCCESS,
	RENEWAL_ISSUE_FAILURE,
	RENEWAL_ISSUE_SUCCESS,
	RENEWAL_QUOTE_FAILURE,
	RENEWAL_QUOTE_SUCCESS,
	RENEWAL_UPDATE_FAILURE,
} from '../data/labelsAndMessages';
import {
	Exposure,
	ExposureCreateRequest,
	FieldGroupUpdateRequest,
	FieldValuesMap,
	Policy2,
} from '../types/policy';
import {
	RenewalActionRequest,
	RenewalCreateRequest,
	RenewalResponse,
	RenewalUpdateRequest,
} from '../types/renewals';
import { getPolicyConfigVersion, isEmpty } from '../util/util';
import { AuthContext, AuthData, HIDE_PERIL_LIMITS_ENABLE } from './auth';
import { ErrorContext, ErrorData } from './ErrorProvider';

export const RenewalContext = createContext<RenewalData>(null);
export const useRenewalContext = () => React.useContext(RenewalContext);

export function withRenewalContext<T>(Component: any) {
	return (props: Omit<T, 'renewalData' | 'authData' | 'errorData'>) => (
		<ErrorContext.Consumer>
			{(errorData) => (
				<RenewalContext.Consumer>
					{(renewalData) => (
						<AuthContext.Consumer>
							{(authData) => (
								<Component
									{...props}
									authData={authData}
									renewalData={renewalData}
									errorData={errorData}
								/>
							)}
						</AuthContext.Consumer>
					)}
				</RenewalContext.Consumer>
			)}
		</ErrorContext.Consumer>
	);
}

export interface RenewalData {
	renewalResponse: RenewalResponse;
	renewalUpdateObj: RenewalResponse;
	policy: Policy2;
	productConfig: ProductConfigurationResponse;
	selectedExposure?: Exposure | ExposureCreateRequest;
	setLoading?: (loading: boolean) => void;
	setRenewalUpdateObj: (
		renewalUpdateObj: RenewalUpdateRequest,
	) => Promise<RenewalData>;
	setRenewalResponse: (
		renewalResponse: RenewalResponse,
	) => Promise<RenewalData>;
	setSelectedExposure: (
		selectedExposure: Exposure | ExposureCreateRequest,
	) => Promise<RenewalData>;
	setPolicy: (policy: Policy2) => Promise<RenewalData>;
	setProductConfig: (
		productConfig: ProductConfigurationResponse,
	) => Promise<RenewalData>;
	updateGroupFieldValueForRenewal: (
		name: string,
		values: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => any;
	loadData: (
		policyId: string,
		renewalId: string,
	) => Promise<RenewalData> | null;
	quoteRenewal: SaveFastForm<boolean>;
	acceptRenewal: SaveFastForm<boolean>;
	issueRenewal: SaveFastForm<boolean>;
	cloneRenewal: () => Promise<RenewalResponse>;
	invalidateRenewal: () => void;
	discardRenewal: () => void;
	fields: FormShape<FormBuilder>;
	saveFastForm: SaveFastForm<RenewalResponse>;
	getFastFormRenewalUpdateObj: (
		values: FieldValues,
		options: SaveOptions,
		dirtyFields: {
			[key: string]: boolean;
		},
	) => RenewalUpdateRequest;
	updateFields: () => Promise<RenewalData>;
	resourcesTimestamps: number[];
}

interface Props {
	children: any;
	authData: AuthData;
	errorData: ErrorData;
}

export class RenewalProvider extends React.Component<Props, RenewalData> {
	constructor(props: any, context: any) {
		super(props);
		this.state = {
			renewalResponse: null,
			fields: null,
			renewalUpdateObj: null,
			policy: null,
			productConfig: null,
			setRenewalUpdateObj: this.setRenewalUpdateObj,
			setRenewalResponse: this.setRenewalResponse,
			setSelectedExposure: this.setSelectedExposure,
			quoteRenewal: this.quoteRenewal,
			acceptRenewal: this.acceptRenewal,
			issueRenewal: this.issueRenewal,
			cloneRenewal: this.cloneRenewal,
			invalidateRenewal: this.invalidateRenewal,
			discardRenewal: this.discardRenewal,
			updateGroupFieldValueForRenewal: this.updateGroupFieldValueForRenewal,
			loadData: this.loadData,
			setPolicy: this.setPolicy,
			setProductConfig: this.setProductConfig,
			saveFastForm: this.saveFastForm,
			getFastFormRenewalUpdateObj: this.getFastFormRenewalUpdateObj,
			updateFields: this.updateFields,
			resourcesTimestamps: [],
		};
	}

	public render() {
		return (
			<RenewalContext.Provider value={this.state}>
				{this.props.children}
			</RenewalContext.Provider>
		);
	}

	private setRenewalUpdateObj = (renewalUpdateObj: RenewalResponse) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ renewalUpdateObj }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setRenewalResponse = (renewalResponse: RenewalResponse) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ renewalResponse }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setSelectedExposure = (selectedExposure: Exposure) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ selectedExposure }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setPolicy = (policy: Policy2) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ policy }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setProductConfig = (productConfig: ProductConfigurationResponse) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ productConfig }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private updateGroupFieldValueForRenewal = (
		name: string,
		fieldGroupValues: FieldValuesMap,
		index?: number,
		fieldGroupLocator?: string,
	) => {
		const { renewalUpdateObj } = this.state;

		if (fieldGroupLocator) {
			// updateFieldGroups
			const updateFieldGroups = renewalUpdateObj.updateFieldGroups || [];
			let updateRequest = updateFieldGroups.find(
				(req: any) => req.fieldGroupLocator === fieldGroupLocator,
			);
			let updateIndex = updateFieldGroups.findIndex(
				(req: any) => req.fieldGroupLocator === fieldGroupLocator,
			);
			updateIndex = updateIndex === -1 ? 0 : updateIndex;

			const newUpdate = {
				fieldGroupLocator,
				fieldName: name,
				// fieldValues: fieldGroupValues,
			} as FieldGroupUpdateRequest;

			updateRequest = updateRequest || newUpdate;
			updateRequest.fieldValues = fieldGroupValues;

			// update field exists but locator doesn't match.
			if (updateRequest) {
				updateFieldGroups[updateIndex] = updateRequest;
			}

			renewalUpdateObj.updateFieldGroups = updateFieldGroups;
			this.setRenewalUpdateObj(renewalUpdateObj);
		} else {
			// add field groups
			this.setRenewalUpdateObj(renewalUpdateObj);
		}
	};

	private updateFields = (): Promise<RenewalData> => {
		const fields = {
			...getRenewalForm({
				renewalData: this.state,
				authData: this.props.authData,
			}),
			...getRenewalExposuresForm({
				renewalData: this.state,
				authData: this.props.authData,
			}),
		};
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ fields }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private setResourcesTimestamps = (resourcesTimestamps: number[]) => {
		const promise = new Promise<RenewalData>((resolve) => {
			this.setState({ resourcesTimestamps }, () => {
				resolve(this.state);
			});
		});
		return promise;
	};

	private getFastFormRenewalUpdateObj = (
		values: FieldValues,
		options: SaveOptions,
		dirtyFields: {
			[key: string]: boolean;
		},
	) => {
		const { renewalUpdateObj, fields } = this.state;
		const { authData } = this.props;
		const hidePerilLimitsEnabled =
			authData?.auth?.configProperties?.[HIDE_PERIL_LIMITS_ENABLE] ?? false;
		const {
			addFieldGroups,
			removeFieldGroups,
			updateFieldGroups,
			addExposures,
			endExposures,
			fieldValues,
			updateExposures,
		} = endorsementRenewalUpdateBuilder(
			dirtyFields,
			values,
			fields,
			'renewalForm',
			{
				...options,
			},
			authData.auth.timezone,
			renewalUpdateObj,
			hidePerilLimitsEnabled,
		);

		// construct update object
		const updateObject: RenewalUpdateRequest = {
			addFieldGroups,
			removeFieldGroups,
			updateFieldGroups,
			addExposures,
			endExposures,
			fieldValues,
			updateExposures,
			renewalEndTimestamp: renewalUpdateObj.renewalEndTimestamp, // from object
			newPaymentScheduleName: renewalUpdateObj.newPaymentScheduleName, // from object
			resetOverrideResourcesAsOfTimestamp:
				renewalUpdateObj.resetOverrideResourcesAsOfTimestamp, // from object
			overrideResourcesAsOfTimestamp:
				renewalUpdateObj.overrideResourcesAsOfTimestamp, // from object
		};

		if (options?.runAutofill) {
			// default specified autofill sequence will run when autofill array is null
			updateObject.autofill = null;
		} else {
			// for regular updates, send an empty array to signify to run no autofills
			updateObject.autofill = [];
		}

		return updateObject;
	};

	/**
	 * Creates update object and sends update request to server (renewalUpdateObj)
	 * @param methods
	 * @param options
	 * @param dirtyFields
	 * @returns Promise<RenewalResponse>
	 */
	private saveFastForm: SaveFastForm<RenewalResponse> = async (
		values,
		dirtyFields,
		reset,
		options,
	) => {
		const { renewalUpdateObj } = this.state;
		const { authData, errorData } = this.props;

		let updatedRenewalUpdateObj = {} as RenewalResponse;
		const updateObject = this.getFastFormRenewalUpdateObj(
			values,
			options,
			dirtyFields,
		);

		// Start saving here:
		try {
			const renewalResponse = await RenewalsApi.updateRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
				{ renewalUpdate: updateObject },
			);
			const renewalResponseData = renewalResponse.data;
			updatedRenewalUpdateObj = renewalResponseData;
			await this.setRenewalResponse(renewalResponseData);
			await this.setRenewalUpdateObj(_.cloneDeep(renewalResponseData));
			await this.updateFields();
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_UPDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return {} as RenewalResponse;
			}
			throw error;
		}

		return updatedRenewalUpdateObj;
	};

	/**
	 * Loads policy and renewal for RenewalRoutes
	 * @param policyId
	 * @param renewalId
	 * @returns Promise<RenewalData>
	 */
	private loadData = async (
		policyId: string,
		renewalId: string,
	): Promise<RenewalData> | null => {
		const { authData, errorData } = this.props;
		if (!authData) {
			return null;
		}
		if (!authData.auth) {
			return null;
		}
		try {
			const policyResponse = await PolicyApi.getPolicy(
				authData.auth.authorizationToken,
				policyId,
			);
			const policy = policyResponse.data as Policy2;
			const policyVersion = getPolicyConfigVersion(policy);
			const pvEnabled =
				authData.auth.configProperties[
					'property.product.versioning.m2.enabled'
				] ||
				authData.auth.configProperties[
					'property.product.versioning.with.repair.enabled'
				];
			let configs = null;
			if (pvEnabled) {
				configs = await ProductApi.getProductsWithVersion(
					authData.auth.authorizationToken,
					policyVersion,
				);
			} else {
				configs = await ProductApi.getProducts(
					authData.auth.authorizationToken,
				);
			}
			const productConfig = configs.data.find(
				(config: ProductConfigurationResponse) =>
					config.name === policy.productName,
			);
			await this.setPolicy(policy);
			await this.setProductConfig(productConfig);
		} catch (error) {
			errorData.handleErrorResponse(error, 'Failed to fetch policy');
			await handleUnauthorizedResponse(error, authData);
			return null;
		}

		try {
			const renewalResponse = await RenewalsApi.getRenewal(
				authData.auth.authorizationToken,
				renewalId,
			);

			if (renewalResponse) {
				const renewal = renewalResponse.data;
				await this.setRenewalResponse(renewal);
				await this.setRenewalUpdateObj(_.cloneDeep(renewal));
				if (renewal.state === 'application') {
					const timestamps = await ResourcesAPI.getResourcesTimestamps(
						this.state.policy.productName,
						authData.auth.authorizationToken,
					);
					await this.setResourcesTimestamps(timestamps);
				}
			} else {
				console.error(LOAD_RENEWAL_FAILURE());
				return null;
			}
		} catch (error) {
			errorData.handleErrorResponse(error, FETCH_RENEWAL_FAILURE());
			await handleUnauthorizedResponse(error, authData);
			return null;
		}
		// shouldn't reach here if a catch block is reached? need to test
		return this.updateFields();
	};

	private quoteRenewal: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
		isDraft,
	) => {
		const {
			renewalUpdateObj,
			setLoading,
			setRenewalUpdateObj,
			setRenewalResponse,
		} = this.state;
		const { authData, errorData } = this.props;

		setLoading(true);
		const renewalUpdate = this.getFastFormRenewalUpdateObj(
			values,
			options,
			dirtyFields,
		);
		const actionReq: RenewalActionRequest = {
			conflictHandling: 'invalidate',
		};
		if (!isEmpty(renewalUpdate)) {
			actionReq.renewalUpdate = renewalUpdate;
			await RenewalsApi.updateRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
				actionReq,
			);
		}

		let result = false;
		try {
			const quotedRenewal = await RenewalsApi.quoteRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
			);
			await setRenewalResponse(quotedRenewal.data);
			await setRenewalUpdateObj(quotedRenewal.data);
			await this.updateFields();
			successToast(RENEWAL_QUOTE_SUCCESS());
			setLoading(false);
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_QUOTE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			console.error(error);
		} finally {
			setLoading(false);
		}
		return result;
	};

	private acceptRenewal: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
		isDraft,
	) => {
		const {
			renewalUpdateObj,
			setLoading,
			setRenewalUpdateObj,
			setRenewalResponse,
		} = this.state;
		const { authData, errorData } = this.props;

		setLoading(true);
		const actionReq: RenewalActionRequest = {
			conflictHandling: 'invalidate',
		};

		if (isDraft) {
			const renewalUpdate = this.getFastFormRenewalUpdateObj(
				values,
				options,
				dirtyFields,
			);
			if (!isEmpty(renewalUpdate)) {
				actionReq.renewalUpdate = renewalUpdate;
				await RenewalsApi.updateRenewal(
					authData.auth.authorizationToken,
					renewalUpdateObj.locator,
					actionReq,
				);
			}
		}

		let result = false;
		try {
			const acceptedRenewal = await RenewalsApi.acceptRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
			);
			await setRenewalResponse(acceptedRenewal.data);
			await setRenewalUpdateObj(acceptedRenewal.data);
			successToast(RENEWAL_ACCEPT_SUCCESS());
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_ACCEPT_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			console.error(error);
		} finally {
			setLoading(false);
		}
		return result;
	};

	private issueRenewal: SaveFastForm<boolean> = async (
		values,
		dirtyFields,
		reset,
		options,
		isDraft,
	) => {
		const {
			renewalUpdateObj,
			setLoading,
			setRenewalUpdateObj,
			setRenewalResponse,
		} = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);
		const actionReq: RenewalActionRequest = {
			conflictHandling: 'invalidate',
		};

		if (isDraft) {
			const renewalUpdate = this.getFastFormRenewalUpdateObj(
				values,
				options,
				dirtyFields,
			);
			if (!isEmpty(renewalUpdate)) {
				actionReq.renewalUpdate = renewalUpdate;
				await RenewalsApi.updateRenewal(
					authData.auth.authorizationToken,
					renewalUpdateObj.locator,
					actionReq,
				);
			}
		}

		let result = false;
		try {
			const issuedRenewal = await RenewalsApi.issueRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
			);
			await setRenewalResponse(issuedRenewal.data);
			await setRenewalUpdateObj(issuedRenewal.data);
			successToast(RENEWAL_ISSUE_SUCCESS());
			result = true;
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_ISSUE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return result;
			}
			throw error;
		} finally {
			setLoading(false);
		}
		return result;
	};

	private cloneRenewal = async () => {
		// Note: use `renewalResponse` instead of `renewalUpdateObj` since `renewalResponse` holds the exact version of renewal from server.
		// `renewalUpdateObj` might hold "dirty" values and not getting saved to server yet.
		const { policy, productConfig, renewalResponse } = this.state;
		const { authData } = this.props;

		// Note: newPaymentScheduleName is part of policy. Only copy it over when there is a value of it
		// in `endorsementResponse`; otherwise, it will result in copying "undefined" value over.
		const newPaymentScheduleName =
			getHasPaymentPluginEnabled(productConfig) &&
			'newPaymentScheduleName' in renewalResponse
				? {
						newPaymentScheduleName: renewalResponse.newPaymentScheduleName,
				  }
				: {};

		const renewalCloneParams: RenewalCreateRequest = {
			renewalEndTimestamp: renewalResponse.renewalEndTimestamp,
			fieldValues: renewalResponse.fieldValues,
			addFieldGroups: renewalResponse.addFieldGroups,
			updateFieldGroups: renewalResponse.updateFieldGroups,
			removeFieldGroups: renewalResponse.removeFieldGroups,
			addExposures: renewalResponse.addExposures,
			updateExposures: renewalResponse.updateExposures,
			endExposures: renewalResponse.endExposures,
			...newPaymentScheduleName,
		};
		try {
			const clonedRenewalResponse = await RenewalsApi.createRenewal(
				authData.auth.authorizationToken,
				policy.locator,
				renewalCloneParams,
			);

			try {
				// attempt to price renewal
				await RenewalsApi.previewRenewalPrice(
					authData.auth.authorizationToken,
					clonedRenewalResponse.data.locator,
				);
			} catch (e) {
				// discard the renewal
				await RenewalsApi.discardRenewal(
					authData.auth.authorizationToken,
					clonedRenewalResponse.data.locator,
				);
				throw e;
			}

			await this.setRenewalResponse(clonedRenewalResponse.data);
			await this.setRenewalUpdateObj(_.cloneDeep(clonedRenewalResponse.data));
			await this.updateFields();

			return clonedRenewalResponse.data;
		} catch (e) {
			throw e;
		}
	};

	private invalidateRenewal = async () => {
		const { renewalUpdateObj, setLoading } = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);
		try {
			await RenewalsApi.invalidateRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
				{ conflictHandling: 'invalidate' },
			);
			successToast(RENEWAL_INVALIDATE_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_INVALIDATE_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			throw error;
		} finally {
			setLoading(false);
		}
	};

	private discardRenewal = async () => {
		const { renewalUpdateObj, setLoading } = this.state;
		const { authData, errorData } = this.props;
		setLoading(true);

		try {
			await RenewalsApi.discardRenewal(
				authData.auth.authorizationToken,
				renewalUpdateObj.locator,
			);
			successToast(RENEWAL_DISCARD_SUCCESS());
		} catch (error) {
			errorData.handleErrorResponse(error, RENEWAL_DISCARD_FAILURE());
			if (await handleUnauthorizedResponse(error, authData)) {
				return;
			}
			throw error;
		} finally {
			setLoading(false);
		}
	};
}

/**
 * Returns the current exposures combined with the current addExposures(type ExposureCreateRequest)
 * minus the removed exposures.
 * @param currentExposures Existing exposures on the policy
 * @returns (Exposure | ExposureCreateRequest)[]
 */
export const getDisplayedExposures = (
	currentExposures: Exposure[],
	renewalUpdateObj: RenewalUpdateRequest,
) => {
	const addedExposures = renewalUpdateObj.addExposures;
	const removedExposures = renewalUpdateObj.endExposures;
	// Locators inside endExposures only match to existing exposures.
	const currentExposuresMinusRemoved = currentExposures.filter((exp) => {
		return !removedExposures.includes((exp as Exposure).locator);
	});

	return (
		currentExposuresMinusRemoved as (Exposure | ExposureCreateRequest)[]
	).concat(addedExposures);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'errorData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<ErrorContext.Consumer>
					{(errorData) => (
						<Component {...props} authData={authData} errorData={errorData} />
					)}
				</ErrorContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(RenewalProvider);
